import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Form,
  Input,
  Select,
  DatePicker,
  Button,
  Space,
  Tag,
  Modal,
  message,
  Row,
  Col,
  Descriptions,
} from "antd";
import { SearchOutlined, ReloadOutlined, EyeOutlined } from "@ant-design/icons";
import { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { MainLayout } from "../../components/layout";
import { TaskService } from "../../services";
import {
  Task,
  TaskQueryParams,
  TaskStatus,
  TaskType,
  TaskStatistics,
  UpdateTaskDto,
} from "../../types/task";
import ErrorHandler from "../../utils/errorHandler";

const { RangePicker } = DatePicker;
const { Option } = Select;

// 任务状态配置
const TASK_STATUS_CONFIG = {
  pending: { label: "待处理", color: "orange" },
  running: { label: "执行中", color: "blue" },
  completed: { label: "已完成", color: "green" },
  failed: { label: "失败", color: "red" },
  expired: { label: "已过期", color: "gray" },
};

// 任务类型配置
const TASK_TYPE_CONFIG = {
  discount_70: { label: "7折优惠", description: "商品7折优惠任务" },
};

const TaskManagement: React.FC = () => {
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [statistics, setStatistics] = useState<TaskStatistics>({
    total: 0,
    pending: 0,
    running: 0,
    completed: 0,
    failed: 0,
  });

  console.log(statistics);

  const [searchParams, setSearchParams] = useState<TaskQueryParams>({});
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  const [editModalVisible, setEditModalVisible] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [currentTask, setCurrentTask] = useState<Task | null>(null);

  // 加载任务列表
  const loadTasks = async (params?: TaskQueryParams) => {
    try {
      setLoading(true);
      const queryParams = {
        ...searchParams,
        ...params,
        page: pagination.current,
        limit: pagination.pageSize,
      };

      const response = await TaskService.getTaskList(queryParams);
      if (response.code === 0 && response.data) {
        const data = response.data;
        setTasks(data.list || []);
        setPagination((prev) => ({
          ...prev,
          total: data.total || 0,
        }));

        // 计算统计数据
        const stats = (data.list || []).reduce(
          (acc, task) => {
            acc.total++;
            acc[task.status]++;
            return acc;
          },
          {
            total: 0,
            pending: 0,
            running: 0,
            completed: 0,
            failed: 0,
          }
        );
        setStatistics(stats);
      }
    } catch (error) {
      ErrorHandler.handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  // 独立的加载函数，用于重置时直接传入参数
  const loadTasksWithParams = async (params: TaskQueryParams) => {
    try {
      setLoading(true);
      const response = await TaskService.getTaskList(params);
      if (response.code === 0 && response.data) {
        const data = response.data;
        setTasks(data.list || []);
        setPagination((prev) => ({
          ...prev,
          total: data.total || 0,
        }));
      }
    } catch (error) {
      ErrorHandler.handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  // 搜索
  const handleSearch = () => {
    const values = form.getFieldsValue();
    const { dateRange, ...otherValues } = values;
    const params: TaskQueryParams = {
      ...otherValues,
      startDate: dateRange?.[0]?.format("YYYY-MM-DD"),
      endDate: dateRange?.[1]?.format("YYYY-MM-DD"),
    };

    setSearchParams(params);
    setPagination((prev) => ({ ...prev, current: 1 }));
    loadTasks(params);
  };

  // 重置搜索
  const handleReset = () => {
    form.resetFields();
    setSearchParams({});
    setPagination((prev) => ({ ...prev, current: 1 }));
    loadTasksWithParams({ page: 1, limit: pagination.pageSize });
  };

  // 查看详情
  const handleViewDetail = (task: Task) => {
    setCurrentTask(task);
    setDetailModalVisible(true);
  };

  // 保存编辑
  const handleSaveEdit = async () => {
    try {
      const values = await editForm.validateFields();
      if (!currentTask) return;

      const updateData: UpdateTaskDto = values;
      await TaskService.updateTask(currentTask.id, updateData);

      message.success("任务更新成功");
      setEditModalVisible(false);
      setCurrentTask(null);
      loadTasks();
    } catch (error) {
      ErrorHandler.handleApiError(error);
    }
  };

  // 表格列配置
  const columns: ColumnsType<Task> = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 80,
    },
    {
      title: "手机号",
      dataIndex: "account",
      key: "account",
      width: 120,
      render: (account: any) => account.phone,
    },
    {
      title: "任务类型",
      dataIndex: "taskType",
      key: "taskType",
      width: 90,
      render: (type: TaskType) => TASK_TYPE_CONFIG[type]?.label || type,
    },
    {
      title: "状态",
      dataIndex: "status",
      key: "status",
      width: 80,
      render: (status: TaskStatus) => {
        const config = TASK_STATUS_CONFIG[status];
        return <Tag color={config?.color}>{config?.label || status}</Tag>;
      },
    },
    {
      title: "创建时间",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 120,
      render: (time: string) => dayjs(time).format("YYYY-MM-DD HH:mm:ss"),
    },
    {
      title: "更新时间",
      dataIndex: "updatedAt",
      key: "updatedAt",
      width: 120,
      render: (time: string) => dayjs(time).format("YYYY-MM-DD HH:mm:ss"),
    },
    {
      title: "操作",
      key: "action",
      width: 70,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetail(record)}
          >
            详情
          </Button>
        </Space>
      ),
    },
  ];

  // 初始化加载
  useEffect(() => {
    const initLoad = async () => {
      try {
        setLoading(true);
        const response = await TaskService.getTaskList({
          page: 1,
          limit: 20,
        });
        if (response.code === 0 && response.data) {
          const data = response.data;
          setTasks(data.list || []);
          setPagination((prev) => ({
            ...prev,
            total: data.total || 0,
          }));

          // 计算统计数据
          const stats = (data.list || []).reduce(
            (acc, task) => {
              acc.total++;
              acc[task.status]++;
              return acc;
            },
            {
              total: 0,
              pending: 0,
              running: 0,
              completed: 0,
              failed: 0,
            }
          );
          setStatistics(stats);
        }
      } catch (error) {
        ErrorHandler.handleApiError(error);
      } finally {
        setLoading(false);
      }
    };
    initLoad();
  }, []);

  // 分页变化
  const handleTableChange = (paginationConfig: any) => {
    const newPagination = {
      current: paginationConfig.current,
      pageSize: paginationConfig.pageSize,
    };
    setPagination((prev) => ({
      ...prev,
      ...newPagination,
    }));

    // 立即加载新页面的数据
    const loadNewPage = async () => {
      try {
        setLoading(true);
        const queryParams = {
          ...searchParams,
          page: newPagination.current,
          limit: newPagination.pageSize,
        };

        const response = await TaskService.getTaskList(queryParams);
        if (response.code === 0 && response.data) {
          const data = response.data;
          setTasks(data.list || []);
          setPagination((prev) => ({
            ...prev,
            total: data.total || 0,
          }));
        }
      } catch (error) {
        ErrorHandler.handleApiError(error);
      } finally {
        setLoading(false);
      }
    };
    loadNewPage();
  };

  return (
    <MainLayout title="任务管理">
      <div className="task-management">
        {/* 搜索表单 */}
        <Card style={{ marginBottom: 24 }}>
          <Form form={form} layout="inline" onFinish={handleSearch}>
            <Form.Item name="phone" label="手机号">
              <Input placeholder="请输入手机号" style={{ width: 150 }} />
            </Form.Item>
            <Form.Item name="taskType" label="任务类型">
              <Select
                placeholder="请选择任务类型"
                style={{ width: 150 }}
                allowClear
              >
                {Object.entries(TASK_TYPE_CONFIG).map(([value, config]) => (
                  <Option key={value} value={value}>
                    {config.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="status" label="状态">
              <Select
                placeholder="请选择状态"
                style={{ width: 120 }}
                allowClear
              >
                {Object.entries(TASK_STATUS_CONFIG).map(([value, config]) => (
                  <Option key={value} value={value}>
                    {config.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="dateRange" label="日期范围">
              <RangePicker style={{ width: 240 }} />
            </Form.Item>
            <Form.Item>
              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SearchOutlined />}
                >
                  搜索
                </Button>
                <Button onClick={handleReset} icon={<ReloadOutlined />}>
                  重置
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Card>

        {/* 任务列表 */}
        <Card>
          <Table
            columns={columns}
            dataSource={tasks}
            rowKey="id"
            loading={loading}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
              pageSizeOptions: ["10", "20", "50"],
            }}
            onChange={handleTableChange}
            scroll={{ x: 1000 }}
          />
        </Card>

        {/* 编辑模态框 */}
        <Modal
          title="编辑任务"
          open={editModalVisible}
          onOk={handleSaveEdit}
          onCancel={() => {
            setEditModalVisible(false);
            setCurrentTask(null);
          }}
          width={600}
        >
          <Form form={editForm} layout="vertical">
            <Form.Item
              name="phone"
              label="手机号"
              rules={[{ required: true, message: "请输入手机号" }]}
            >
              <Input placeholder="请输入手机号" />
            </Form.Item>
            <Form.Item
              name="taskType"
              label="任务类型"
              rules={[{ required: true, message: "请选择任务类型" }]}
            >
              <Select placeholder="请选择任务类型">
                {Object.entries(TASK_TYPE_CONFIG).map(([value, config]) => (
                  <Option key={value} value={value}>
                    {config.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              name="status"
              label="状态"
              rules={[{ required: true, message: "请选择状态" }]}
            >
              <Select placeholder="请选择状态">
                {Object.entries(TASK_STATUS_CONFIG).map(([value, config]) => (
                  <Option key={value} value={value}>
                    {config.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="remark" label="备注">
              <Input.TextArea rows={3} placeholder="请输入备注" />
            </Form.Item>
          </Form>
        </Modal>

        {/* 详情模态框 */}
        <Modal
          title="任务详情"
          open={detailModalVisible}
          onCancel={() => {
            setDetailModalVisible(false);
            setCurrentTask(null);
          }}
          footer={null}
          width={600}
        >
          {currentTask && (
            <div>
              <Row gutter={16}>
                <Col span={12}>
                  <p>
                    <strong>任务ID:</strong> {currentTask.id}
                  </p>
                </Col>
                <Col span={12}>
                  <p>
                    <strong>手机号:</strong> {currentTask.account.phone}
                  </p>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={12}>
                  <p>
                    <strong>任务类型:</strong>{" "}
                    {TASK_TYPE_CONFIG[currentTask.taskType]?.label ||
                      currentTask.taskType}
                  </p>
                </Col>
                <Col span={12}>
                  <p>
                    <strong>状态:</strong>{" "}
                    <Tag color={TASK_STATUS_CONFIG[currentTask.status]?.color}>
                      {TASK_STATUS_CONFIG[currentTask.status]?.label ||
                        currentTask.status}
                    </Tag>
                  </p>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <p>
                    <strong>任务开始时间:</strong> {currentTask.startTime}
                  </p>
                </Col>
                <Col span={12}>
                  <p>
                    <strong>任务结束时间:</strong> {currentTask.endTime}
                  </p>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <p>
                    <strong>创建时间:</strong>{" "}
                    {dayjs(currentTask.createdAt).format("YYYY-MM-DD HH:mm:ss")}
                  </p>
                </Col>
                <Col span={12}>
                  <p>
                    <strong>更新时间:</strong>{" "}
                    {dayjs(currentTask.updatedAt).format("YYYY-MM-DD HH:mm:ss")}
                  </p>
                </Col>
              </Row>
              {currentTask.remark && (
                <Row gutter={16}>
                  <Col span={24}>
                    <p>
                      <strong>备注:</strong> {currentTask.remark}
                    </p>
                  </Col>
                </Row>
              )}
              {
                <Row gutter={16}>
                  <Col span={12}>
                    <p>
                      <strong>消耗token:</strong>{" "}
                      {currentTask.tokenLog?.operationAmount || "无消耗"}
                    </p>
                  </Col>
                </Row>
              }

              {currentTask.ext && Object.keys(currentTask.ext).length > 0 && (
                <Row gutter={16} style={{ marginTop: 16 }}>
                  <Col span={24}>
                    <div>
                      <strong
                        style={{
                          fontSize: "14px",
                          marginBottom: "8px",
                          display: "block",
                        }}
                      >
                        扩展信息:
                      </strong>
                      <Descriptions
                        bordered
                        size="small"
                        column={1}
                        style={{ marginTop: 8 }}
                      >
                        {Object.entries(currentTask.ext).map(([key, value]) => (
                          <Descriptions.Item key={key} label={key}>
                            {typeof value === "object" ? (
                              <pre
                                style={{
                                  margin: 0,
                                  padding: "8px",
                                  backgroundColor: "#f5f5f5",
                                  borderRadius: "4px",
                                  fontSize: "12px",
                                  maxHeight: "200px",
                                  overflow: "auto",
                                }}
                              >
                                {JSON.stringify(value, null, 2)}
                              </pre>
                            ) : (
                              <span>{String(value)}</span>
                            )}
                          </Descriptions.Item>
                        ))}
                      </Descriptions>
                    </div>
                  </Col>
                </Row>
              )}
            </div>
          )}
        </Modal>
      </div>
    </MainLayout>
  );
};

export default TaskManagement;
